import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Pagination, CircularProgress, Alert, Dialog,
  DialogActions, TableSortLabel, LinearProgress, Collapse, Divider,
  Badge, Checkbox, Slide, Fade, Chip,
} from "@mui/material";
import SearchIcon          from "@mui/icons-material/Search";
import AddIcon             from "@mui/icons-material/Add";
import EditIcon            from "@mui/icons-material/Edit";
import DeleteIcon          from "@mui/icons-material/Delete";
import VisibilityIcon      from "@mui/icons-material/Visibility";
import PictureAsPdfIcon    from "@mui/icons-material/PictureAsPdf";
import CalendarTodayIcon   from "@mui/icons-material/CalendarToday";
import CloseIcon           from "@mui/icons-material/Close";
import TuneIcon            from "@mui/icons-material/Tune";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import SelectAllIcon       from "@mui/icons-material/SelectAll";
import DeselectIcon        from "@mui/icons-material/Deselect";
import FileUploadIcon      from "@mui/icons-material/FileUpload";
import FileDownloadIcon    from "@mui/icons-material/FileDownload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import EditNoteIcon        from "@mui/icons-material/EditNote";
import api from "../../../services/api";

const C = {
  primary:    "#2563EB", primaryBg:  "#EFF6FF",
  surface:    "#FFFFFF", surfaceAlt: "#F8FAFC",
  border:     "#E2E8F0", borderHov:  "#CBD5E1",
  text1:      "#0F172A", text2:      "#475569", text3: "#94A3B8",
  success:    "#16A34A", successBg:  "#F0FDF4", successBdr: "#BBF7D0",
  warn:       "#D97706", warnBg:     "#FFFBEB", warnBdr:    "#FDE68A",
  danger:     "#DC2626", dangerBg:   "#FEF2F2", dangerBdr:  "#FECACA",
  teal:       "#0F766E", tealBg:     "#F0FDFA", tealBdr:    "#99F6E4",
};

const thSx = {
  fontSize: "0.7rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`,
  py: "10px", px: "12px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};
const tdSx = {
  py: "10px", px: "12px",
  borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle",
};
const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

function ActionBtn({ title, icon, hoverColor, hoverBg, onClick }) {
  return (
    <Tooltip title={title} arrow>
      <IconButton size="small" onClick={onClick} sx={{
        width: 28, height: 28, borderRadius: "6px", color: C.text3,
        transition: "all 0.15s",
        "&:hover": { color: hoverColor, backgroundColor: hoverBg },
      }}>
        {icon}
      </IconButton>
    </Tooltip>
  );
}

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

function StockBadge({ stock, unit }) {
  const num = Number(stock ?? 0);
  const color   = num === 0 ? C.danger  : num <= 5 ? C.warn    : C.success;
  const bgColor = num === 0 ? C.dangerBg: num <= 5 ? C.warnBg  : C.successBg;
  const border  = num === 0 ? C.dangerBdr: num <= 5 ? C.warnBdr : C.successBdr;

  return (
    <Box sx={{
      display: "inline-flex", alignItems: "baseline", gap: "3px",
      px: "8px", py: "2px", borderRadius: "6px",
      backgroundColor: bgColor, border: `1px solid ${border}`,
    }}>
      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
        {num}
      </Typography>
      {unit && (
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 500, color, opacity: 0.8 }}>
          {unit}
        </Typography>
      )}
    </Box>
  );
}

function BulkActionBar({ selectedIds, onClear, onBulkDelete, onBulkExportPdf, bulkDeleting, bulkExporting }) {
  const count = selectedIds.length;
  return (
    <Slide direction="up" in={count > 0} mountOnEnter unmountOnExit>
      <Box sx={{
        position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
        zIndex: 1300, display: "flex", alignItems: "center", gap: 1.5,
        px: 2.5, py: 1.5, backgroundColor: C.text1, borderRadius: "14px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
        minWidth: 420, border: `1px solid rgba(255,255,255,0.08)`,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 0.5, backgroundColor: C.primary, borderRadius: "8px", flexShrink: 0 }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff", lineHeight: 1 }}>
            {count} dipilih
          </Typography>
        </Box>
        <Box sx={{ flex: 1, height: 1, backgroundColor: "rgba(255,255,255,0.1)" }} />
        <Button size="small"
          startIcon={bulkExporting ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <PictureAsPdfIcon sx={{ fontSize: "14px !important" }} />}
          onClick={onBulkExportPdf} disabled={bulkExporting || bulkDeleting}
          sx={{ textTransform: "none", fontSize: "0.78rem", fontWeight: 500,
            color: "#E2E8F0", borderRadius: "8px", height: 32, px: 1.5,
            border: "1px solid rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.08)", color: "#fff" },
            "&:disabled": { color: "rgba(255,255,255,0.3)" } }}>
          {bulkExporting ? "Mengekspor..." : "Export PDF"}
        </Button>
        <Button size="small"
          startIcon={bulkDeleting ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <DeleteIcon sx={{ fontSize: "14px !important" }} />}
          onClick={onBulkDelete} disabled={bulkDeleting || bulkExporting}
          sx={{ textTransform: "none", fontSize: "0.78rem", fontWeight: 600,
            color: "#fff", borderRadius: "8px", height: 32, px: 1.5,
            backgroundColor: C.danger, "&:hover": { backgroundColor: "#B91C1C" },
            "&:disabled": { backgroundColor: "rgba(220,38,38,0.4)", color: "rgba(255,255,255,0.5)" } }}>
          {bulkDeleting ? "Menghapus..." : "Hapus Semua"}
        </Button>
        <Tooltip title="Batal pilih" arrow>
          <IconButton size="small" onClick={onClear} sx={{
            color: "rgba(255,255,255,0.5)", width: 28, height: 28, borderRadius: "6px",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Slide>
  );
}

export default function AtkItems() {
  const navigate = useNavigate();

  const [items,           setItems]           = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");
  const [page,            setPage]            = useState(1);
  const [totalPages,      setTotalPages]      = useState(1);
  const [totalItems,      setTotalItems]      = useState(0);
  const [searchInput,     setSearchInput]     = useState("");
  const [search,          setSearch]          = useState("");
  const [sortOrder,       setSortOrder]       = useState("desc");
  const [dateFrom,        setDateFrom]        = useState("");
  const [dateTo,          setDateTo]          = useState("");
  const [deleteId,        setDeleteId]        = useState(null);
  const [deleteItem,      setDeleteItem]      = useState(null);
  const [deleting,        setDeleting]        = useState(false);
  const [exportingPdf,    setExportingPdf]    = useState(false);
  const [showFilters,     setShowFilters]     = useState(false);
  const [selectedIds,     setSelectedIds]     = useState([]);
  const [bulkDeleting,    setBulkDeleting]    = useState(false);
  const [bulkExporting,   setBulkExporting]   = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const [importOpen,   setImportOpen]   = useState(false);
  const [importFile,   setImportFile]   = useState(null);
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const debounceRef  = useRef(null);

  const currentPageIds   = items.map(i => i.id);
  const allPageSelected  = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.includes(id));
  const somePageSelected = currentPageIds.some(id => selectedIds.includes(id)) && !allPageSelected;

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  useEffect(() => { setSelectedIds([]); }, [page, search, sortOrder, dateFrom, dateTo]);

  const handleSelectAll = () => {
    if (allPageSelected) setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
    else setSelectedIds(prev => [...new Set([...prev, ...currentPageIds])]);
  };
  const handleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page, per_page: 10, sort: sortOrder };
      if (search)   params.search    = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const res = await api.get("/atk-items", { params });
      setItems(res.data.data.data          ?? []);
      setTotalPages(res.data.data.last_page ?? 1);
      setTotalItems(res.data.data.total     ?? 0);
    } catch {
      setError("Gagal memuat data ATK.");
    } finally { setLoading(false); }
  }, [page, search, sortOrder, dateFrom, dateTo]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setPage(1); }, [sortOrder, dateFrom, dateTo]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/atk-items/${deleteId}`);
      setDeleteId(null); setDeleteItem(null);
      fetchItems();
    } catch { setError("Gagal menghapus ATK."); }
    finally  { setDeleting(false); }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true); setBulkConfirmOpen(false);
    try {
      await api.post("/atk-items/bulk-delete", { ids: selectedIds });
      setSelectedIds([]); fetchItems();
    } catch (err) { setError(err.response?.data?.message || "Gagal menghapus ATK yang dipilih."); }
    finally { setBulkDeleting(false); }
  };

  const handleBulkExportPdf = async () => {
    setBulkExporting(true);
    try {
      const res = await api.post("/atk-items/bulk-export-pdf", { ids: selectedIds }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `atk-pilihan-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { setError("Gagal mengekspor PDF ATK yang dipilih."); }
    finally  { setBulkExporting(false); }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = {};
      if (search)   params.search    = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const res = await api.get("/atk-items/export-pdf", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `daftar-atk-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { setError("Gagal mengekspor PDF."); }
    finally  { setExportingPdf(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get("/atk-items/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = "template-import-atk.xlsx";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { setError("Gagal mengunduh template."); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await api.post("/atk-items/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      setImportFile(null);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || "Gagal mengimpor file.");
    } finally { setImporting(false); }
  };

  const closeImport = () => {
    if (importing) return;
    setImportOpen(false); setImportResult(null); setImportFile(null);
  };

  const handleClearDates = () => { setDateFrom(""); setDateTo(""); };
  const toggleSort = () => setSortOrder(p => p === "desc" ? "asc" : "desc");

  const hasDateFilter = dateFrom || dateTo;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.tealBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EditNoteIcon sx={{ fontSize: 20, color: C.teal }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: C.text1, lineHeight: 1.2 }}>
                Daftar ATK
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
                Kelola alat tulis kantor yang telah dibeli
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Button variant="outlined" size="small"
              startIcon={exportingPdf ? <CircularProgress size={13} sx={{ color: C.danger }} /> : <PictureAsPdfIcon sx={{ fontSize: "15px !important" }} />}
              onClick={handleExportPdf} disabled={exportingPdf}
              sx={{ borderColor: C.dangerBdr, color: C.danger, textTransform: "none",
                borderRadius: "8px", fontSize: "0.8rem", fontWeight: 500, height: 34,
                "&:hover": { borderColor: C.danger, backgroundColor: C.dangerBg } }}>
              {exportingPdf ? "Mengekspor..." : "Export PDF"}
            </Button>

            <Button variant="outlined" size="small"
              startIcon={<FileUploadIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}
              sx={{ borderColor: C.border, color: C.text2, textTransform: "none",
                borderRadius: "8px", fontSize: "0.8rem", fontWeight: 500, height: 34,
                "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg } }}>
              Import Excel
            </Button>

            <Button variant="contained" size="small"
              startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/atk/items/create`)}
              sx={{ backgroundColor: C.teal, color: "#fff", textTransform: "none",
                borderRadius: "8px", fontSize: "0.8rem", fontWeight: 600, height: 34, px: 2,
                boxShadow: "0 4px 12px rgba(15,118,110,0.2)",
                "&:hover": { backgroundColor: "#0F766E", boxShadow: "0 6px 16px rgba(15,118,110,0.3)" } }}>
              Tambah ATK
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Alert ── */}
      {error && (
        <Alert severity="error" onClose={() => setError("")}
          sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.dangerBdr}` }}>
          {error}
        </Alert>
      )}

      {/* ── Filter Card ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px",
        backgroundColor: C.surface, mb: 2, overflow: "hidden" }}>
        <Box sx={{ p: "12px 16px", display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <TextField size="small" placeholder="Cari nama ATK..."
            value={searchInput} onChange={handleSearchInput}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: C.text3 }} /></InputAdornment>,
              endAdornment: loading && searchInput ? <InputAdornment position="end"><CircularProgress size={13} sx={{ color: C.primary }} /></InputAdornment> : null,
            }}
            sx={{ ...inputBase, flex: "1 1 220px", maxWidth: 320 }} />

          <Button size="small" variant={showFilters ? "contained" : "outlined"}
            startIcon={<TuneIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => setShowFilters(p => !p)}
            sx={{ height: 34, borderRadius: "8px", textTransform: "none", fontSize: "0.8rem", fontWeight: 500,
              ...(showFilters
                ? { backgroundColor: C.primary, "&:hover": { backgroundColor: "#1D4ED8" } }
                : { borderColor: C.border, color: C.text2, "&:hover": { borderColor: C.primary, color: C.primary } }) }}>
            Filter Tanggal
          </Button>

          {hasDateFilter && (
            <Button size="small" onClick={handleClearDates}
              sx={{ fontSize: "0.72rem", color: C.text3, textTransform: "none",
                p: "0 4px", minWidth: 0, "&:hover": { color: C.danger } }}>
              Hapus filter
            </Button>
          )}
        </Box>

        <Collapse in={showFilters}>
          <Divider sx={{ borderColor: C.border }} />
          <Box sx={{ p: "12px 16px", display: "flex", gap: 1.5, flexWrap: "wrap",
            alignItems: "center", backgroundColor: C.surfaceAlt }}>
            <TextField size="small" type="date" label="Dari tanggal" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); if (dateTo && e.target.value > dateTo) setDateTo(""); }}
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 13, color: dateFrom ? C.primary : C.text3 }} /></InputAdornment> }}
              sx={{ ...inputBase, width: 170 }} />
            <TextField size="small" type="date" label="Sampai tanggal" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateFrom || undefined }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 13, color: dateTo ? C.primary : C.text3 }} /></InputAdornment> }}
              sx={{ ...inputBase, width: 170 }} />
            {hasDateFilter && (
              <Tooltip title="Reset tanggal">
                <IconButton size="small" onClick={handleClearDates}
                  sx={{ border: `1px solid ${C.dangerBdr}`, color: C.danger,
                    borderRadius: "8px", width: 32, height: 32, "&:hover": { backgroundColor: C.dangerBg } }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* ── Table Card ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ px: 2, py: "10px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: `1px solid ${C.border}`,
          backgroundColor: selectedIds.length > 0 ? C.primaryBg : C.surfaceAlt, transition: "background 0.2s" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text3, fontWeight: 500 }}>
              {loading ? "Memuat data..." : totalItems > 0
                ? `${(page - 1) * 10 + 1}–${Math.min(page * 10, totalItems)} dari ${totalItems} item ATK`
                : "0 item ATK"}
            </Typography>
            {selectedIds.length > 0 && (
              <Fade in>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: C.primary }} />
                  <Typography sx={{ fontSize: "0.75rem", color: C.primary, fontWeight: 600 }}>
                    {selectedIds.length} item dipilih
                  </Typography>
                  <Button size="small" onClick={() => setSelectedIds([])}
                    sx={{ fontSize: "0.7rem", color: C.text3, textTransform: "none",
                      p: "0 4px", minWidth: 0, "&:hover": { color: C.danger } }}>
                    Batalkan
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loading && (
              <LinearProgress sx={{ width: 80, height: 3, borderRadius: 4,
                backgroundColor: C.border, "& .MuiLinearProgress-bar": { backgroundColor: C.primary } }} />
            )}
            {!loading && items.length > 0 && (
              <Tooltip title={allPageSelected ? "Batal pilih halaman ini" : "Pilih semua di halaman ini"} arrow>
                <IconButton size="small" onClick={handleSelectAll}
                  sx={{ width: 28, height: 28, borderRadius: "6px",
                    color: allPageSelected ? C.primary : C.text3,
                    backgroundColor: allPageSelected ? C.primaryBg : "transparent",
                    border: `1px solid ${allPageSelected ? C.primary : C.border}`,
                    "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg } }}>
                  {allPageSelected ? <DeselectIcon sx={{ fontSize: 14 }} /> : <SelectAllIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                <TableCell sx={{ ...thSx, width: 44, px: "8px" }}>
                  <Checkbox size="small" checked={allPageSelected} indeterminate={somePageSelected}
                    onChange={handleSelectAll} disabled={loading || items.length === 0}
                    sx={{ p: 0.5, color: C.text3, "&.Mui-checked": { color: C.primary },
                      "&.MuiCheckbox-indeterminate": { color: C.primary } }} />
                </TableCell>
                <TableCell sx={{ ...thSx, width: 50 }}>
                  <TableSortLabel active direction={sortOrder} onClick={toggleSort}
                    sx={{ fontSize: "0.7rem", fontWeight: 700, color: `${C.text3} !important`,
                      "& .MuiTableSortLabel-icon": { fontSize: 13, color: `${C.text3} !important` } }}>
                    {sortOrder === "desc" ? "Terbaru" : "Terlama"}
                  </TableSortLabel>
                </TableCell>
                {["Nama Barang", "Stok", "Tgl. Beli", "Aksi"].map(h => (
                  <TableCell key={h} sx={thSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8, border: 0 }}>
                    <CircularProgress size={28} thickness={4} sx={{ color: C.teal }} />
                    <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: C.text3 }}>Memuat data...</Typography>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8, border: 0 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: "12px", backgroundColor: C.tealBg,
                      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <EditNoteIcon sx={{ fontSize: 24, color: C.teal }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.875rem", color: C.text2, fontWeight: 500 }}>
                      {search ? `Tidak ditemukan hasil untuk "${search}"` : "Belum ada data ATK"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.5 }}>
                      {search ? "Coba kata kunci lain atau hapus filter" : 'Klik "Tambah ATK" untuk mulai'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : items.map((item, idx) => {
                const isSelected = selectedIds.includes(item.id);
                return (
                  <TableRow key={item.id} sx={{
                    "&:hover": { backgroundColor: isSelected ? "#EFF6FF" : C.surfaceAlt },
                    "&:hover .row-actions": { opacity: 1 },
                    backgroundColor: isSelected ? "#F0F7FF" : "transparent",
                    borderBottom: `1px solid ${C.border}`, transition: "background 0.12s",
                  }}>
                    <TableCell sx={{ ...tdSx, px: "8px", width: 44 }}>
                      <Checkbox size="small" checked={isSelected} onChange={() => handleSelectOne(item.id)}
                        sx={{ p: 0.5, color: C.text3, "&.Mui-checked": { color: C.primary } }} />
                    </TableCell>
                    {/* No */}
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {(page - 1) * 10 + idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.825rem", fontWeight: 600, color: C.text1, whiteSpace: "nowrap" }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    {/* Stok */}
                    <TableCell sx={tdSx}>
                      <StockBadge stock={item.stock} unit={item.unit} />
                    </TableCell>
                    {/* Tgl. Beli */}
                    <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>
                        {fmtDate(item.purchase_date ?? item.created_at)}
                      </Typography>
                    </TableCell>
                    {/* Aksi */}
                    <TableCell sx={tdSx}>
                      <Box className="row-actions" sx={{ display: "flex", gap: 0.5, opacity: { xs: 1, md: 0.4 }, transition: "opacity 0.15s" }}>
                        <ActionBtn title="Lihat Detail" icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                          hoverColor={C.primary} hoverBg={C.primaryBg}
                          onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/atk/${item.id}`)} />
                        <ActionBtn title="Edit" icon={<EditIcon sx={{ fontSize: 14 }} />}
                          hoverColor={C.warn} hoverBg={C.warnBg}
                          onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/atk/items/${item.id}/edit`)} />
                        <ActionBtn title="Hapus" icon={<DeleteIcon sx={{ fontSize: 14 }} />}
                          hoverColor={C.danger} hoverBg={C.dangerBg}
                          onClick={() => { setDeleteId(item.id); setDeleteItem(item); }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            px: 2, py: "10px", borderTop: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
            <Typography sx={{ fontSize: "0.75rem", color: C.text3 }}>
              Halaman {page} dari {totalPages}
            </Typography>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
              size="small" color="primary"
              sx={{ "& .MuiPaginationItem-root": { fontSize: "0.75rem", borderRadius: "6px", minWidth: 28, height: 28 },
                "& .Mui-selected": { backgroundColor: `${C.primary} !important`, color: "#fff", fontWeight: 600 } }} />
          </Box>
        )}
      </Paper>

      {/* ── Single Delete Dialog ── */}
      <Dialog open={Boolean(deleteId)} onClose={() => { setDeleteId(null); setDeleteItem(null); }}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <DeleteIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>Hapus ATK?</Typography>
              {deleteItem && (
                <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                  <strong>{deleteItem.name}</strong> akan dihapus secara permanen dan tidak bisa dipulihkan.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => { setDeleteId(null); setDeleteItem(null); }}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>Batal</Button>
          <Button onClick={handleDelete} disabled={deleting} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.danger, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            {deleting ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Hapus"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Delete Confirm Dialog ── */}
      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <DeleteIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Hapus {selectedIds.length} ATK?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua ATK yang dipilih akan dihapus secara permanen dan tidak bisa dipulihkan.
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setBulkConfirmOpen(false)}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>Batal</Button>
          <Button onClick={handleBulkDelete} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.danger, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            Hapus Semua
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Import Excel Modal ── */}
      <Dialog open={importOpen} onClose={closeImport} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "14px", boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ px: 3, pt: 3, pb: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "9px", backgroundColor: C.tealBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FileUploadIcon sx={{ fontSize: 18, color: C.teal }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.text1, lineHeight: 1.2 }}>
                Import ATK via Excel
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>
                Format: .xlsx atau .xls, maks. 5MB
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={closeImport}
            sx={{ color: C.text3, borderRadius: "6px", "&:hover": { backgroundColor: C.surfaceAlt } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Box sx={{ p: "12px 14px", borderRadius: "10px", backgroundColor: C.tealBg,
            border: `1px solid ${C.tealBdr}`, mb: 2.5,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: C.teal }}>
                Belum punya template?
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: C.text2, mt: 0.25 }}>
                Download template Excel yang sudah sesuai format sistem
              </Typography>
            </Box>
            <Button size="small" variant="outlined"
              startIcon={<FileDownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleDownloadTemplate}
              sx={{ borderColor: C.teal, color: C.teal, textTransform: "none",
                borderRadius: "8px", fontSize: "0.78rem", fontWeight: 600, whiteSpace: "nowrap",
                "&:hover": { backgroundColor: C.teal, color: "#fff" } }}>
              Download Template
            </Button>
          </Box>

          <Box onClick={() => !importing && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${importFile ? C.teal : C.border}`,
              borderRadius: "10px", p: 3, textAlign: "center",
              backgroundColor: importFile ? C.tealBg : C.surfaceAlt,
              cursor: importing ? "not-allowed" : "pointer", transition: "all 0.2s",
              "&:hover": !importing ? { borderColor: C.teal, backgroundColor: C.tealBg } : {},
            }}>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden
              onChange={(e) => { if (e.target.files[0]) { setImportFile(e.target.files[0]); setImportResult(null); }}} />
            {importFile ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                <InsertDriveFileIcon sx={{ fontSize: 28, color: C.teal }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: C.text1 }}>
                    {importFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>
                    {(importFile.size / 1024).toFixed(1)} KB · Klik untuk ganti file
                  </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                  sx={{ color: C.text3, "&:hover": { color: C.danger } }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : (
              <>
                <FileUploadIcon sx={{ fontSize: 32, color: C.text3, mb: 1 }} />
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: C.text2 }}>
                  Klik untuk pilih file
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: C.text3, mt: 0.5 }}>
                  Mendukung .xlsx, .xls, .csv
                </Typography>
              </>
            )}
          </Box>

          {importResult && (
            <Box sx={{ mt: 2, p: "12px 14px", borderRadius: "10px",
              backgroundColor: importResult.errors?.length > 0 ? C.warnBg : C.successBg,
              border: `1px solid ${importResult.errors?.length > 0 ? C.warnBdr : C.successBdr}` }}>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700,
                color: importResult.errors?.length > 0 ? C.warn : C.success, mb: 0.5 }}>
                {importResult.errors?.length > 0 ? "Import selesai dengan beberapa error" : "Import berhasil!"}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>
                {importResult.inserted} ATK ditambahkan · {importResult.updated} diperbarui
                {importResult.errors?.length > 0 && ` · ${importResult.errors.length} baris error`}
              </Typography>
              {importResult.errors?.length > 0 && (
                <Box sx={{ mt: 1.5, maxHeight: 120, overflowY: "auto" }}>
                  {importResult.errors.map((e, i) => (
                    <Typography key={i} sx={{ fontSize: "0.72rem", color: C.danger, lineHeight: 1.6 }}>
                      Baris {e.row} ({e.code}): {e.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={closeImport}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            {importResult ? "Tutup" : "Batal"}
          </Button>
          {!importResult && (
            <Button onClick={handleImport} disabled={!importFile || importing} variant="contained"
              startIcon={importing
                ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                : <FileUploadIcon sx={{ fontSize: "15px !important" }} />}
              sx={{ textTransform: "none", borderRadius: "8px", px: 2,
                backgroundColor: C.teal, fontWeight: 600, boxShadow: "none",
                "&:hover": { backgroundColor: "#0F766E", boxShadow: "none" },
                "&:disabled": { backgroundColor: C.border } }}>
              {importing ? "Mengimpor..." : "Import Sekarang"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Floating Bulk Action Bar ── */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds([])}
        onBulkDelete={() => setBulkConfirmOpen(true)}
        onBulkExportPdf={handleBulkExportPdf}
        bulkDeleting={bulkDeleting}
        bulkExporting={bulkExporting}
      />
    </Box>
  );
}
